// src/pages/Features/FeaturesPage.tsx
// Rich, persuasive features page — modelled on antigravity.google/product
// Deep-dive sections with use cases, key facts, and visual context

import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import { seoConfig } from '../../config/seo';
import {
    FileText, FileDown, Combine, QrCode, Scan, Type, Image, FileJson,
    Database, Webhook, Mail, CreditCard,
    BookOpen, GraduationCap,
    Shield, Zap, Globe, Lock,
    CheckCircle, HelpCircle, ArrowRight, LucideIcon, Users, Code2,
} from 'lucide-react';
import { m, AnimatePresence } from 'framer-motion';
import { Typography } from '../../components/ui/typography';

// ── Types ────────────────────────────────────────────────────────────────────

interface DeepDiveCard {
    id: string;
    icon: LucideIcon;
    title: string;
    tagline: string;
    description: string;
    useCases: string[];
    keyFacts: { label: string; value: string }[];
    link: string;
    cta: string;
    accentColor: string;
    accentBg: string;
}

// ── Tools Data ───────────────────────────────────────────────────────────────

const toolCards: DeepDiveCard[] = [
    {
        id: 'text-compare',
        icon: FileText,
        title: 'Text Comparison Tool',
        tagline: 'The most private diff tool on the web',
        description:
            'Compare two documents, code snippets, or data files with character-level precision — entirely in your browser. The intelligent diff engine highlights every addition, deletion, and modification with color-coded overlays. Used daily by developers for code review, lawyers for contract revisions, and editors for proofreading.',
        useCases: [
            'Review pull request diffs without uploading code to a third party',
            'Compare two versions of a legal contract before signing',
            'Proofread content edits — see exactly what changed between drafts',
            'Audit configuration files for accidental changes',
        ],
        keyFacts: [
            { label: 'Precision', value: 'Character-level' },
            { label: 'Privacy', value: '100% local' },
            { label: 'File size', value: 'Unlimited' },
        ],
        link: '/tools/compare-text',
        cta: 'Open Text Compare',
        accentColor: '#1a73e8',
        accentBg: '#e8f0fe',
    },
    {
        id: 'file-reducer',
        icon: FileDown,
        title: 'File Size Reducer',
        tagline: 'Compress up to 80% — quality stays',
        description:
            'Reduce PDF, image, and document file sizes using adaptive compression algorithms that analyse each file type and apply optimal settings automatically. No fixed quality presets — the engine targets the best size/quality balance for your specific file. Ideal before emailing, uploading to a portal, or archiving.',
        useCases: [
            'Reduce a scanned PDF from 15 MB to under 2 MB for email',
            'Optimise product photos for fast page loads without visible loss',
            'Compress Word documents before submitting to an online portal',
            'Batch-reduce a folder of images for storage savings',
        ],
        keyFacts: [
            { label: 'Max reduction', value: '~80%' },
            { label: 'Formats', value: 'PDF · JPG · PNG · DOCX' },
            { label: 'Mode', value: 'Auto + manual' },
        ],
        link: '/tools/compress-pdf-images',
        cta: 'Compress Files',
        accentColor: '#1e8e3e',
        accentBg: '#e6f4ea',
    },
    {
        id: 'merge-split',
        icon: Combine,
        title: 'PDF Merge & Split',
        tagline: 'Full page-level control over your PDFs',
        description:
            'Merge multiple PDF files into a single ordered document, or split a large PDF into precise page ranges. Drag-and-drop reordering lets you set the exact page sequence before merging. Extract just the pages you need from a 200-page report. Maintain vector quality throughout — no re-rendering, no resolution loss.',
        useCases: [
            'Consolidate monthly reports into a single annual document',
            'Extract one chapter from a large ebook or manual',
            'Merge scanned receipts into one file for expense reporting',
            'Split a large form pack into individual client files',
        ],
        keyFacts: [
            { label: 'Operation', value: 'Merge & split' },
            { label: 'Page control', value: 'Drag to reorder' },
            { label: 'Quality', value: 'Vector preserved' },
        ],
        link: '/tools/merge-split-pdf',
        cta: 'Merge or Split PDF',
        accentColor: '#9334e6',
        accentBg: '#f3e8fd',
    },
    {
        id: 'qr-generator',
        icon: QrCode,
        title: 'QR Code Generator',
        tagline: 'Custom QR codes with brand identity',
        description:
            'Generate fully customised QR codes for any URL, plain text, WiFi credentials, or contact card. Embed your logo, set brand colours, choose error-correction level, and export high-resolution PNG or crisp SVG. QR codes never expire — there is no server to go down because the code itself encodes all the data.',
        useCases: [
            'Add QR codes to business cards that link to your portfolio',
            'Print WiFi credentials on a card for guests — no password sharing',
            'Put a QR code on product packaging to link to a how-to video',
            'Generate event ticket QR codes for check-ins',
        ],
        keyFacts: [
            { label: 'Output', value: 'PNG · SVG' },
            { label: 'Logo embed', value: 'Supported' },
            { label: 'Expiry', value: 'Never' },
        ],
        link: '/tools/qr-code-generator',
        cta: 'Generate QR Code',
        accentColor: '#f9ab00',
        accentBg: '#fef7e0',
    },
    {
        id: 'ocr',
        icon: Type,
        title: 'OCR Text Extractor',
        tagline: 'AI text recognition across 13 languages',
        description:
            'Extract editable, searchable text from any image, screenshot, or scanned document using an advanced AI recognition engine. Handles printed text with near-perfect accuracy and has strong support for handwriting. Processes multi-page PDFs page by page. Outputs clean, formatted text ready for editing or search indexing.',
        useCases: [
            'Extract text from a scanned contract so you can search and edit it',
            'Digitise lecture notes from a whiteboard photo',
            'Convert a printed form into editable data without retyping',
            'Pull product information from a photo of a receipt or label',
        ],
        keyFacts: [
            { label: 'Languages', value: '13+' },
            { label: 'Handwriting', value: 'Supported' },
            { label: 'Multi-page', value: 'PDF supported' },
        ],
        link: '/tools/scan-image-to-text',
        cta: 'Extract Text',
        accentColor: '#1a73e8',
        accentBg: '#e8f0fe',
    },
    {
        id: 'bg-remover',
        icon: Image,
        title: 'AI Background Remover',
        tagline: 'Professional-grade cutouts in one click',
        description:
            'Remove backgrounds automatically using industrial-grade AI edge detection that distinguishes subjects from complex backgrounds — hair, fur, and fine edges included. Produces clean transparent PNGs immediately. No manual masking, no Photoshop required. Outputs are ready for product listings, presentations, or composite designs.',
        useCases: [
            'Create clean product images for an e-commerce listing',
            'Make profile photos with a transparent or custom background',
            'Prepare assets for a presentation without a photo editor',
            'Design marketing banners by compositing subjects onto new backgrounds',
        ],
        keyFacts: [
            { label: 'Output', value: 'Transparent PNG' },
            { label: 'Edge quality', value: 'AI hair & fur' },
            { label: 'Skill needed', value: 'None' },
        ],
        link: '/tools/remove-background',
        cta: 'Remove Background',
        accentColor: '#1e8e3e',
        accentBg: '#e6f4ea',
    },
    {
        id: 'barcode-scanner',
        icon: Scan,
        title: 'Barcode & QR Scanner',
        tagline: 'Decode any code — camera or image upload',
        description:
            'Decode barcodes and QR codes directly from your browser using the device camera in real time or by uploading an image file. Supports every major format used in retail, logistics, and events. No app to install, no hardware needed beyond a phone or webcam. Decoded results can be copied, exported, or followed as a link.',
        useCases: [
            'Look up a product from its barcode while shopping',
            'Scan event tickets for check-ins from any browser',
            'Decode a QR code from a screenshot without a phone',
            'Verify a printed barcode reads correctly before going to print',
        ],
        keyFacts: [
            { label: 'Formats', value: 'QR · EAN · UPC · Code 128' },
            { label: 'Input', value: 'Camera or image' },
            { label: 'App needed', value: 'Never' },
        ],
        link: '/tools/barcode-scanner',
        cta: 'Scan a Code',
        accentColor: '#f9ab00',
        accentBg: '#fef7e0',
    },
    {
        id: 'json-validator',
        icon: FileJson,
        title: 'JSON Validator & Power Tool',
        tagline: 'Validate, format, query — entirely local',
        description:
            'Validate and format JSON with real-time syntax highlighting and actionable error reporting. Smart Auto-Fix corrects the most common mistakes — missing commas, unquoted keys, trailing commas — automatically. JSONPath querying lets you extract specific values from deeply nested structures. Schema validation against a JSON Schema draft. All processing happens locally: your API responses and config files never leave your browser.',
        useCases: [
            'Debug a malformed API response before filing a bug report',
            'Pretty-print minified JSON from a network response',
            'Extract a nested field value across a large JSON config',
            'Validate a JSON file against a schema before deploying',
        ],
        keyFacts: [
            { label: 'Auto-fix', value: 'Smart repair' },
            { label: 'Querying', value: 'JSONPath' },
            { label: 'Privacy', value: '100% local' },
        ],
        link: '/tools/json-validator-formatter',
        cta: 'Open JSON Tool',
        accentColor: '#9334e6',
        accentBg: '#f3e8fd',
    },
];

// ── Dev Hub Data ─────────────────────────────────────────────────────────────

const devCards: DeepDiveCard[] = [
    {
        id: 'mock-api',
        icon: Database,
        title: 'Mock API',
        tagline: 'Professional REST sandbox with chaos engineering',
        description:
            'A stateless REST API sandbox that generates realistic, schema-driven response data on every request. Configure delay, HTTP error codes, and chaos parameters (random 5xx injection, network simulation) to stress-test your frontend against real failure modes. No keys, no registration — endpoint is live the moment you open it.',
        useCases: [
            'Prototype a new frontend feature before the real API is built',
            'Test loading states and skeleton UIs with configurable delays',
            'Build E2E tests that always get deterministic data',
            'Demo a product to a client without needing a live backend',
        ],
        keyFacts: [
            { label: 'Data gen', value: 'Auto schema' },
            { label: 'Chaos', value: 'Configurable' },
            { label: 'Auth', value: 'None needed' },
        ],
        link: '/dev-hub/mock-api',
        cta: 'Launch Mock API',
        accentColor: '#1a73e8',
        accentBg: '#e8f0fe',
    },
    {
        id: 'webhook-tester',
        icon: Webhook,
        title: 'Webhook Tester',
        tagline: 'Real-time HTTP callback inspector',
        description:
            'Point any service at your personal webhook endpoint and watch every incoming request arrive in a live log — headers, body, query params, and timing. Define custom response rules so your webhook replies exactly as your integration expects. Essential for debugging CI/CD pipelines, payment callbacks, and third-party integrations.',
        useCases: [
            'Verify a Stripe webhook fires correctly after a test payment',
            'Debug a GitHub Actions webhook without deploying a server',
            'Inspect the exact payload structure a new SaaS sends',
            'Test webhook retry logic by returning configurable error codes',
        ],
        keyFacts: [
            { label: 'Log', value: 'Live real-time' },
            { label: 'Responses', value: 'Configurable rules' },
            { label: 'Body', value: 'JSON & form data' },
        ],
        link: '/dev-hub/webhook-tester',
        cta: 'Open Webhook Tester',
        accentColor: '#1e8e3e',
        accentBg: '#e6f4ea',
    },
    {
        id: 'email-sandbox',
        icon: Mail,
        title: 'Email Sandbox',
        tagline: 'Black-hole SMTP — preview without sending',
        description:
            'A disposable SMTP server that catches outbound emails from your development or staging environment and renders them in a preview inbox. Inspect full HTML rendering, check spam scores, verify attachments, and analyse headers — without ever sending a message to a real recipient. Prevents the classic "oops, sent 500 test emails to customers" incident.',
        useCases: [
            'Preview a new email template across simulated clients',
            'Verify transactional emails fire correctly after a user action',
            'Check spam headers before switching to a production SMTP service',
            'Test bulk sends on staging without a real email quota',
        ],
        keyFacts: [
            { label: 'Real emails', value: 'Never sent' },
            { label: 'HTML preview', value: 'Full render' },
            { label: 'Spam score', value: 'Included' },
        ],
        link: '/dev-hub/email-sandbox',
        cta: 'Open Email Sandbox',
        accentColor: '#9334e6',
        accentBg: '#f3e8fd',
    },
    {
        id: 'payment-simulator',
        icon: CreditCard,
        title: 'Payment Simulator',
        tagline: 'Full payment lifecycle — zero financial risk',
        description:
            'Simulate the complete payment intent lifecycle — create, confirm, fail, refund — in a stateless sandbox that dispatches realistic webhook events to your server. Trigger fraud scenarios, network timeouts, and card decline codes on demand. Build and harden your checkout flow without touching a test account or risking real transactions.',
        useCases: [
            'Test how your order page handles a card decline gracefully',
            'Verify your refund logic fires the correct webhook handler',
            'Simulate a payment network timeout and check retry behaviour',
            'Load-test checkout without generating real payment records',
        ],
        keyFacts: [
            { label: 'Scenarios', value: 'Fraud · timeout · decline' },
            { label: 'Webhooks', value: 'Dispatched' },
            { label: 'Risk', value: 'Zero' },
        ],
        link: '/dev-hub/payment-simulator',
        cta: 'Open Payment Simulator',
        accentColor: '#f9ab00',
        accentBg: '#fef7e0',
    },
];

// ── Insights Data ────────────────────────────────────────────────────────────

const insightCards: DeepDiveCard[] = [
    {
        id: 'articles',
        icon: BookOpen,
        title: 'Engineering Guides',
        tagline: 'Peer-reviewed technical depth, free forever',
        description:
            'In-depth long-form articles on the technologies powering FileFusion and modern software engineering at large. Topics include diff algorithm theory (Myers vs Patience), PDF internal architecture, OCR accuracy techniques, image compression mathematics, and security-conscious file handling. Written by practising engineers and updated as standards evolve.',
        useCases: [
            'Understand why your diff tool chose a specific change region',
            'Learn how PDF compression works before trusting a tool with sensitive documents',
            'Study OCR accuracy trade-offs before integrating it into a product',
            'Deep-dive into file format internals for an engineering interview',
        ],
        keyFacts: [
            { label: 'Depth', value: 'Long-form analysis' },
            { label: 'Authors', value: 'Practising engineers' },
            { label: 'Cost', value: 'Free, no paywall' },
        ],
        link: '/insights/articles',
        cta: 'Browse Articles',
        accentColor: '#1a73e8',
        accentBg: '#e8f0fe',
    },
    {
        id: 'courses',
        icon: GraduationCap,
        title: 'Programming Courses',
        tagline: 'Structured learning paths from zero to production',
        description:
            'Structured engineering courses covering Python, JavaScript, HTML5, and C. Each course progresses from core fundamentals to advanced topics — pointers and memory management in C, async/await patterns in JavaScript, DOM APIs in HTML5, and data science with Python. Code examples follow modern language standards and are tested against current runtime versions.',
        useCases: [
            'Master JavaScript async patterns before writing your first API client',
            'Understand C memory management before taking a systems programming role',
            'Learn Python from scratch to automate repetitive file tasks',
            'Get up to speed with HTML5 APIs for a frontend project quickly',
        ],
        keyFacts: [
            { label: 'Languages', value: 'Python · JS · HTML5 · C' },
            { label: 'Structure', value: 'Progressive paths' },
            { label: 'Code', value: 'Tested & current' },
        ],
        link: '/insights/courses',
        cta: 'Start Learning',
        accentColor: '#1e8e3e',
        accentBg: '#e6f4ea',
    },
];

// ── Deep Dive Card Component ─────────────────────────────────────────────────

interface DeepDiveProps {
    card: DeepDiveCard;
    index: number;
    flip?: boolean;
}

const DeepDiveSection: React.FC<DeepDiveProps> = ({ card, index, flip = false }) => {
    const Icon = card.icon;
    return (
        <m.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: (index % 2) * 0.08 }}
            className="bg-white border border-[#e8eaed] rounded-[32px] overflow-hidden hover:shadow-lg transition-shadow duration-300"
        >
            <div className={`grid grid-cols-1 lg:grid-cols-2 ${flip ? 'lg:flex-row-reverse' : ''}`}>
                {/* Left: content */}
                <div className="p-8 md:p-12 flex flex-col justify-between">
                    <div>
                        {/* Icon + title */}
                        <div className="flex items-start gap-4 mb-6">
                            <div
                                className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 mt-0.5"
                                style={{ backgroundColor: card.accentBg, color: card.accentColor }}
                            >
                                <Icon size={24} strokeWidth={1.5} />
                            </div>
                            <div>
                                <h3 className="text-2xl font-medium text-[#121317] leading-snug">{card.title}</h3>
                                <p className="text-[0.9rem] mt-1" style={{ color: card.accentColor }}>{card.tagline}</p>
                            </div>
                        </div>

                        {/* Description */}
                        <p className="text-[0.9375rem] text-[#5f6368] leading-[1.75] mb-8">{card.description}</p>

                        {/* Key facts strip */}
                        <div className="flex flex-wrap gap-3 mb-8">
                            {card.keyFacts.map(f => (
                                <div
                                    key={f.label}
                                    className="flex flex-col px-4 py-2.5 rounded-2xl border border-[#e8eaed] bg-[#f8f9fa]"
                                >
                                    <span className="text-[0.7rem] font-medium text-[#80868b] uppercase tracking-wide">{f.label}</span>
                                    <span className="text-[0.9rem] font-medium text-[#121317]">{f.value}</span>
                                </div>
                            ))}
                        </div>

                        {/* CTA */}
                        <Link
                            to={card.link}
                            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-medium text-white no-underline transition-all hover:opacity-90 hover:shadow-md text-[0.9375rem]"
                            style={{ backgroundColor: card.accentColor }}
                        >
                            {card.cta}
                            <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>

                {/* Right: use-cases panel */}
                <div
                    className="p-8 md:p-12 flex flex-col justify-center"
                    style={{ backgroundColor: card.accentBg + '55' }}
                >
                    <h4 className="text-[0.75rem] font-semibold uppercase tracking-widest mb-5" style={{ color: card.accentColor }}>
                        Common use cases
                    </h4>
                    <ul className="space-y-4 list-none p-0 m-0">
                        {card.useCases.map((uc, i) => (
                            <li key={i} className="flex items-start gap-3">
                                <CheckCircle
                                    size={16}
                                    className="shrink-0 mt-0.5"
                                    style={{ color: card.accentColor }}
                                />
                                <span className="text-[0.9rem] text-[#3c4043] leading-snug">{uc}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </m.div>
    );
};

// ── Section Header ────────────────────────────────────────────────────────────

interface SectionHeaderProps {
    icon: LucideIcon;
    iconColor: string;
    iconBg: string;
    eyebrow: string;
    title: string;
    subtitle: string;
    seeAll: string;
    seeAllLabel: string;
}

const SectionHeader: React.FC<SectionHeaderProps> = ({
    icon: Icon, iconColor, iconBg, eyebrow, title, subtitle, seeAll, seeAllLabel
}) => (
    <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
        <div className="max-w-2xl">
            <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: iconBg, color: iconColor }}>
                    <Icon size={18} strokeWidth={1.5} />
                </div>
                <span className="text-[0.8rem] font-semibold uppercase tracking-widest" style={{ color: iconColor }}>{eyebrow}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-light text-[#121317] mb-3">{title}</h2>
            <p className="text-[#5f6368] text-lg leading-relaxed">{subtitle}</p>
        </div>
        <Link
            to={seeAll}
            className="flex items-center gap-1.5 font-medium text-[0.95rem] no-underline hover:underline transition-colors shrink-0 group"
            style={{ color: iconColor }}
        >
            {seeAllLabel}
            <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
        </Link>
    </div>
);

// ── Page ──────────────────────────────────────────────────────────────────────

const FeaturesPage: React.FC = () => {
    return (
        <>
            <SEO {...seoConfig.features} />

            <div className="min-h-screen bg-white font-brand text-[#202124]">
                <main>

                    {/* ── Hero ──────────────────────────────────────────────────── */}
                    <header className="max-w-7xl mx-auto px-6 pt-16 pb-20 text-center">
                        <Typography variant="hero-title" as="h1" weight="light" className="mb-6 text-[#121317]">
                            The complete{' '}
                            <span className="text-[#1a73e8]">FileFusion</span>{' '}
                            platform
                        </Typography>
                        <Typography variant="hero-subtitle" weight="normal" className="max-w-2xl mx-auto text-[#5f6368] mb-10">
                            Professional digital tools, developer sandbox services, and engineering education —
                            all free, all private, no account required.
                        </Typography>
                        <div className="flex flex-wrap gap-4 justify-center">
                            <Link
                                to="/tools"
                                className="inline-flex items-center gap-2 px-7 py-3 bg-[#1a73e8] text-white font-medium rounded-full no-underline hover:bg-[#1557b0] transition-colors shadow-sm"
                            >
                                Start with Tools <ArrowRight size={17} />
                            </Link>
                            <Link
                                to="/dev-hub"
                                className="inline-flex items-center gap-2 px-7 py-3 bg-white border border-[#dadce0] text-[#3c4043] font-medium rounded-full no-underline hover:bg-[#f8f9fa] transition-colors"
                            >
                                Developer Hub
                            </Link>
                        </div>
                    </header>

                    {/* ── Platform Stat Strip ───────────────────────────────────── */}
                    <section className="max-w-7xl mx-auto px-6 pb-20 border-b border-[#f1f3f4]">
                        <div className="flex justify-center gap-12 sm:gap-24 flex-wrap">
                            {[
                                { value: '8', label: 'Professional Tools' },
                                { value: '4', label: 'Developer Services' },
                                { value: '2', label: 'Learning Tracks' },
                                { value: '100%', label: 'Free Forever' },
                            ].map(s => (
                                <div key={s.label} className="flex flex-col items-center text-center">
                                    <div className="text-[2.75rem] font-light text-[#121317] leading-none mb-2">{s.value}</div>
                                    <div className="text-[0.875rem] text-[#5f6368]">{s.label}</div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* ── Professional Tools ────────────────────────────────────── */}
                    <section className="max-w-7xl mx-auto px-6 py-20">
                        <SectionHeader
                            icon={FileText}
                            iconColor="#1a73e8"
                            iconBg="#e8f0fe"
                            eyebrow="Professional Tools"
                            title="Eight tools for everyone"
                            subtitle="Browser-based, private, and built for real workflows — from quick tasks to professional pipelines."
                            seeAll="/tools"
                            seeAllLabel="Browse all tools"
                        />
                        <div className="flex flex-col gap-6">
                            {toolCards.map((card, i) => (
                                <DeepDiveSection key={card.id} card={card} index={i} flip={i % 2 !== 0} />
                            ))}
                        </div>
                    </section>

                    {/* ── Developer Hub ─────────────────────────────────────────── */}
                    <section className="bg-[#fafafa] border-t border-[#f1f3f4] py-20">
                        <div className="max-w-7xl mx-auto px-6">
                            <SectionHeader
                                icon={Code2}
                                iconColor="#1e8e3e"
                                iconBg="#e6f4ea"
                                eyebrow="Developer Hub"
                                title="Sandbox infrastructure, no setup"
                                subtitle="Professional-grade services for testing, debugging, and de-risking your integrations before they hit production."
                                seeAll="/dev-hub"
                                seeAllLabel="Explore Dev Hub"
                            />
                            <div className="flex flex-col gap-6">
                                {devCards.map((card, i) => (
                                    <DeepDiveSection key={card.id} card={card} index={i} flip={i % 2 !== 0} />
                                ))}
                            </div>
                        </div>
                    </section>

                    {/* ── Insights ──────────────────────────────────────────────── */}
                    <section className="max-w-7xl mx-auto px-6 py-20 border-t border-[#f1f3f4]">
                        <SectionHeader
                            icon={BookOpen}
                            iconColor="#f9ab00"
                            iconBg="#fef7e0"
                            eyebrow="Knowledge & Learning"
                            title="Engineering depth, open to all"
                            subtitle="Long-form technical articles and structured programming courses — peer-reviewed, up to date, and permanently free."
                            seeAll="/insights"
                            seeAllLabel="Explore Insights"
                        />
                        <div className="flex flex-col gap-6">
                            {insightCards.map((card, i) => (
                                <DeepDiveSection key={card.id} card={card} index={i} flip={i % 2 !== 0} />
                            ))}
                        </div>
                    </section>

                    {/* ── Platform Qualities ────────────────────────────────────── */}
                    <section className="bg-[#fafafa] border-t border-[#f1f3f4] py-20">
                        <div className="max-w-7xl mx-auto px-6">
                            <div className="bg-white rounded-[32px] border border-[#e8eaed] p-8 md:p-16 grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
                                {[
                                    {
                                        icon: Shield, color: '#1a73e8', bg: '#e8f0fe',
                                        title: 'Enterprise Security',
                                        desc: 'Files processed with encryption and deleted immediately. Browser-based tools never upload your data. Your documents are yours alone.',
                                    },
                                    {
                                        icon: Zap, color: '#1e8e3e', bg: '#e6f4ea',
                                        title: 'Instant Performance',
                                        desc: 'Adaptive algorithms, chunked processing for large inputs, and hardware acceleration where available. Results in seconds, not minutes.',
                                    },
                                    {
                                        icon: Globe, color: '#f9ab00', bg: '#fef7e0',
                                        title: 'Universal Access',
                                        desc: 'Every modern browser on every platform — Windows, Mac, Linux, iOS, Android. No install, no account, no restrictions.',
                                    },
                                ].map((q, i) => {
                                    const Icon = q.icon;
                                    return (
                                        <m.div
                                            key={i}
                                            initial={{ opacity: 0, y: 20 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            viewport={{ once: true }}
                                            transition={{ duration: 0.5, delay: i * 0.1 }}
                                            className="flex flex-col items-center"
                                        >
                                            <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6 shadow-sm" style={{ backgroundColor: q.bg }}>
                                                <Icon size={26} style={{ color: q.color }} />
                                            </div>
                                            <h3 className="text-xl font-medium text-[#121317] mb-3">{q.title}</h3>
                                            <p className="text-[#5f6368] leading-relaxed max-w-xs">{q.desc}</p>
                                        </m.div>
                                    );
                                })}
                            </div>
                        </div>
                    </section>

                    {/* ── Why FileFusion ────────────────────────────────────────── */}
                    <section className="max-w-7xl mx-auto px-6 py-20 border-t border-[#f1f3f4]">
                        <div className="mb-12">
                            <h2 className="text-3xl md:text-4xl font-light text-[#121317] mb-3">Why FileFusion?</h2>
                            <p className="text-[#5f6368] text-lg">Three principles that never get compromised</p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {[
                                {
                                    icon: Users, color: '#1e8e3e', bg: '#e6f4ea',
                                    title: 'No Sign-Up, Ever',
                                    desc: 'Every tool, service, and article is available immediately — no account, no email, no credit card. Access is not gated behind a registration wall.',
                                },
                                {
                                    icon: Lock, color: '#1a73e8', bg: '#e8f0fe',
                                    title: 'Your Data Stays Yours',
                                    desc: 'Files are never stored on our servers after processing. Browser-based tools never upload your data at all. We cannot see what you process.',
                                },
                                {
                                    icon: Zap, color: '#f9ab00', bg: '#fef7e0',
                                    title: '100% Free, No Limits',
                                    desc: 'No freemium tier, no paywalled features, no usage caps. The full platform — all tools and all services — is free forever for everyone.',
                                },
                            ].map((c, i) => {
                                const Icon = c.icon;
                                return (
                                    <m.div
                                        key={i}
                                        initial={{ opacity: 0, y: 20 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ duration: 0.5, delay: i * 0.08 }}
                                        className="bg-white border border-[#e8eaed] rounded-[32px] p-8"
                                    >
                                        <div className="w-12 h-12 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: c.bg }}>
                                            <Icon size={22} style={{ color: c.color }} />
                                        </div>
                                        <h3 className="text-xl font-medium text-[#121317] mb-3">{c.title}</h3>
                                        <p className="text-[#5f6368] leading-relaxed">{c.desc}</p>
                                    </m.div>
                                );
                            })}
                        </div>
                    </section>

                    {/* ── FAQ CTA ───────────────────────────────────────────────── */}
                    <section className="bg-[#fafafa] border-t border-[#f1f3f4] py-20">
                        <div className="max-w-7xl mx-auto px-6">
                            <div className="flex flex-col md:flex-row items-center justify-between gap-12 bg-white border border-[#e8eaed] rounded-[32px] p-8 md:p-16">
                                <div className="max-w-xl text-center md:text-left">
                                    <div className="w-12 h-12 bg-[#e8f0fe] rounded-2xl flex items-center justify-center text-[#1a73e8] mb-6 mx-auto md:mx-0">
                                        <HelpCircle size={24} />
                                    </div>
                                    <h2 className="text-3xl font-light text-[#121317] mb-4">Still have questions?</h2>
                                    <p className="text-[#5f6368] text-lg leading-relaxed">
                                        Detailed answers about privacy, security, file limits, and browser compatibility are in our help centre.
                                    </p>
                                </div>
                                <div className="flex flex-col items-center gap-4 shrink-0">
                                    <Link
                                        to="/faq"
                                        className="flex items-center gap-2 px-6 py-2.5 bg-[#1a73e8] text-white rounded-full font-medium hover:bg-[#1557b0] transition-colors shadow-sm no-underline"
                                    >
                                        Browse all FAQs
                                    </Link>
                                    <div className="flex flex-wrap gap-3 justify-center">
                                        {[
                                            { to: '/tools', label: 'All Tools' },
                                            { to: '/dev-hub', label: 'Developer Hub' },
                                            { to: '/insights', label: 'Insights' },
                                        ].map(l => (
                                            <Link
                                                key={l.to}
                                                to={l.to}
                                                className="text-sm font-medium text-[#5f6368] hover:text-[#121317] px-4 py-2 bg-[#f8f9fa] rounded-full border border-[#dadce0] transition-colors no-underline"
                                            >
                                                {l.label}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>

                </main>
            </div>
        </>
    );
};

export default FeaturesPage;
